import { MilestoneData, MilestoneStatus, Objective, ObjectivesMetadata } from '../types/milestones';
import { getConfiguredSubawardeeIdsForComponentProject } from '../config/componentProjects';
import httpClient from './httpClient';

const scopedHeaders = (groupScope: string) => ({
  headers: { 'X-Selected-Group': groupScope },
});

const componentProjectHeaders = {
  headers: { 'X-Admin-Scope': 'all' },
};

const adminScopeHeaders = {
  headers: { 'X-Admin-Scope': 'all' },
};

type AdminObjectivesListResponse = {
  items: Array<{
    subawardee_id: string;
    objectives: Objective[];
    metadata?: ObjectivesMetadata | null;
  }>;
};

function tagObjectivesWithSubawardee(subawardeeId: string, objectives: Objective[]): Objective[] {
  return objectives.map((objective) => ({
    ...objective,
    key_activities: objective.key_activities.map((keyActivity) => ({
      ...keyActivity,
      subawardee: keyActivity.subawardee ?? subawardeeId,
      goals: keyActivity.goals.map((goal) => ({
        ...goal,
        activities: goal.activities.map((activity) => ({
          ...activity,
          milestones: activity.milestones.map((milestone) => ({
            ...milestone,
            subawardee: milestone.subawardee ?? subawardeeId,
          })),
        })),
      })),
    })),
  }));
}

function mergeAdminObjectivesResponse(data: AdminObjectivesListResponse): MilestoneData {
  const objectives: Objective[] = [];
  const metadataBySubawardee: Record<string, ObjectivesMetadata | null> = {};

  for (const item of data.items ?? []) {
    objectives.push(...tagObjectivesWithSubawardee(item.subawardee_id, item.objectives ?? []));
    metadataBySubawardee[item.subawardee_id] = item.metadata ?? null;
  }

  return { objectives, metadataBySubawardee };
}

function readMetadataBySubawardee(
  value: unknown
): Record<string, ObjectivesMetadata | null> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return value as Record<string, ObjectivesMetadata | null>;
}

function normalizeObjectivesResponse(data: unknown): MilestoneData {
  if (!data || typeof data !== 'object') {
    return { objectives: [] };
  }

  const payload = data as Record<string, unknown>;

  if (Array.isArray(payload.items)) {
    return mergeAdminObjectivesResponse(payload as AdminObjectivesListResponse);
  }

  const objectives = Array.isArray(payload.objectives) ? (payload.objectives as Objective[]) : [];
  const metadata =
    (payload.metadata as ObjectivesMetadata | null | undefined) ?? undefined;
  const metadataBySubawardee =
    readMetadataBySubawardee(payload.metadataBySubawardee) ??
    readMetadataBySubawardee(payload.metadata_by_subawardee);

  return { objectives, metadata, metadataBySubawardee };
}

function hasObjectivesMetadata(data: MilestoneData): boolean {
  if (data.metadata?.updated_at) return true;
  if (!data.metadataBySubawardee) return false;
  return Object.values(data.metadataBySubawardee).some((entry) => Boolean(entry?.updated_at));
}

async function fetchMetadataBySubawardee(
  subawardeeIds: string[]
): Promise<Record<string, ObjectivesMetadata | null>> {
  if (subawardeeIds.length === 0) return {};

  const response = await httpClient.get<AdminObjectivesListResponse>('/v1/admin/objectives', {
    params: { subawardee: subawardeeIds.join(',') },
    ...adminScopeHeaders,
  });

  const metadataBySubawardee: Record<string, ObjectivesMetadata | null> = {};
  for (const item of response.data.items ?? []) {
    metadataBySubawardee[item.subawardee_id] = item.metadata ?? null;
  }
  return metadataBySubawardee;
}

export async function getMilestonesForAllSubawardees(subawardeeIds: string[]): Promise<MilestoneData> {
  if (subawardeeIds.length === 0) {
    return { objectives: [] };
  }
  try {
    const response = await httpClient.get<AdminObjectivesListResponse>('/v1/admin/objectives', {
      params: { subawardee: subawardeeIds.join(',') },
      ...adminScopeHeaders,
    });
    return mergeAdminObjectivesResponse(response.data);
  } catch (error) {
    console.error('Error fetching admin objectives:', error);
    throw error;
  }
}

export const milestonesApi = {
  getMilestones: async (): Promise<MilestoneData> => {
    try {
      const response = await httpClient.get('/v1/objectives/');
      return normalizeObjectivesResponse(response.data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
      throw error;
    }
  },

  /** Same as getMilestones but scopes the request to a specific subawardee (admin). */
  getMilestonesForSubawardee: async (subawardee: string): Promise<MilestoneData> => {
    try {
      const response = await httpClient.get('/v1/objectives/', scopedHeaders(subawardee));
      return normalizeObjectivesResponse(response.data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
      throw error;
    }
  },

  /** Admin all-subawardee milestone trees (optionally narrowed by friendly subawardee names). */
  getMilestonesForAllSubawardees,

  getMilestonesForComponentProject: async (componentProjectId: string): Promise<MilestoneData> => {
    try {
      const response = await httpClient.get(
        `/v1/component-projects/${encodeURIComponent(componentProjectId)}/objectives`,
        componentProjectHeaders
      );
      const milestoneData = normalizeObjectivesResponse(response.data);

      if (!hasObjectivesMetadata(milestoneData)) {
        try {
          const subawardeeIds = getConfiguredSubawardeeIdsForComponentProject(componentProjectId);
          milestoneData.metadataBySubawardee = await fetchMetadataBySubawardee(subawardeeIds);
        } catch (metadataError) {
          console.error('Error fetching component project objectives metadata:', metadataError);
        }
      }

      return milestoneData;
    } catch (error) {
      console.error('Error fetching component project milestones:', error);
      throw error;
    }
  },
  getUpcomingMilestones: async (): Promise<MilestoneData> => {
    try {
      const response = await httpClient.get('/v1/objectives/upcoming-milestones');
      return response.data;
    } catch (error) {
      console.error('Error fetching milestones:', error);
      throw error;
    }
  },
  updateMilestoneStatus: async (
    milestoneId: string,
    status: MilestoneStatus,
    pathContext: {
      objective_id: string;
      key_activity_id: string;
      goal_id: string;
      activity_id: string;
    },
    subawardeeForScope?: string
  ): Promise<void> => {
    try {
      await httpClient.patch(
        `/v1/objectives/milestones/${milestoneId}`,
        {
          path: pathContext,
          update: {
            status
          }
        },
        subawardeeForScope ? scopedHeaders(subawardeeForScope) : undefined
      );
    } catch (error) {
      console.error('Error updating milestone status:', error);
      throw error;
    }
  },

  /** Same payload as updateMilestoneStatus; scoped by component project. */
  updateComponentProjectMilestoneStatus: async (
    componentProjectId: string,
    objectiveId: string,
    status: MilestoneStatus,
    pathContext: {
      objective_id: string;
      key_activity_id: string;
      goal_id: string;
      activity_id: string;
    }
  ): Promise<void> => {
    try {
      await httpClient.patch(
        `/v1/component-projects/${encodeURIComponent(componentProjectId)}/objectives/${encodeURIComponent(objectiveId)}`,
        {
          path: pathContext,
          update: {
            status
          }
        },
        componentProjectHeaders
      );
    } catch (error) {
      console.error('Error updating component project milestone status:', error);
      throw error;
    }
  }
}; 